// ============================================================
// Dataset Types
// ============================================================

export type DatasetStatus = 'active' | 'archived' | 'processing';

// ===========================
// Filter Types
// ===========================

/**
 * Document relevance ranking configuration.
 * When enabled, documents are scored based on ES content analysis
 * for cancer-relevant signals and filtered by minimum score threshold.
 */
export interface DocRankerFilter {
    /** Enable document relevance ranking */
    enabled?: boolean;
    /** Minimum relevance score threshold (0-100). Documents below this score are excluded. */
    minScore?: number;
    /** Optional cancer type filter for scoring. Boosts documents matching this cancer type. */
    cancerType?: 'prostate' | 'bladder' | 'lung' | 'breast' | 'colorectal' | 'pancreatic';
}

export interface DocumentFilter {
    noteTypeConcepts?: number[];
    noteDateStart?: string;
    noteDateEnd?: string;
    providerIds?: number[];
    visitOccurrenceIds?: number[];
    noteSourceValues?: string[];
    // Condition-based filtering (OMOP) - using ICD codes
    conditionIcdCodes?: string[]; // Include filter - documents with these conditions
    excludeConditionIcdCodes?: string[]; // Exclude filter - documents without these conditions
    procedureIcdCodes?: string[];
    conditionKeyword?: string;
    /**
     * Document IDs to exclude from the results.
     * These are IDs that were manually excluded by the user via checkbox.
     */
    excludedIds?: number[];
    /**
     * Optional logical groups to allow AND/OR nesting in document filters.
     * When provided, the backend should use these groups to build the query.
     */
    groups?: DocumentFilterGroup[];
    /**
     * If true, fill each patient's document count to minimumDocumentCount by randomly selecting
     * additional documents from that patient's other documents (not matching the filter).
     */
    fillToMinDocumentCount?: boolean;
    /**
     * Minimum document count per patient. If a patient has fewer documents matching the filter,
     * additional documents will be randomly selected to reach this count (if fillToMinDocumentCount is true).
     */
    minDocumentCount?: number;
    /**
     * If true, hide patients that have no matching documents after all filters are applied.
     * This differs from fillToMinDocumentCount which keeps all patients and fills with random docs.
     */
    hideEmptyPatients?: boolean;
    /**
     * Document relevance ranking configuration. When enabled, documents are scored based on
     * ES content analysis for cancer-relevant signals and filtered by minimum score threshold.
     */
    docRanker?: DocRankerFilter;
}

export type DocumentFilterField =
    | 'conditionKeyword'
    | 'conditionIcdCodes'
    | 'procedureIcdCodes'
    | 'noteTypeConcepts'
    | 'noteDateRange'
    | 'noteSourceValues';

export interface DocumentFilterCondition {
    field: DocumentFilterField;
    value?: any;
    /**
     * Operator that connects this condition with the previous one in the same group.
     * Defaults to AND when omitted.
     */
    conditionOperator?: 'AND' | 'OR';
    /**
     * IN/OUT flags for multi-select values (e.g., ICD include/exclude in the same condition row).
     */
    valueStatuses?: Record<string, 'IN' | 'OUT'>;
}

export interface DocumentFilterGroup {
    operator: 'AND' | 'OR';
    conditions: DocumentFilterCondition[];
}

export interface ImageFilter {
    imagingSourceId?: string;
    datastoreId?: string;
    modalities?: string[];
    bodyParts?: string[];
    studyDateStart?: string;
    studyDateEnd?: string;
    patientId?: string;
    studyDescription?: string;
    /**
     * Optional logical groups to allow AND/OR nesting in image filters.
     * When provided, the backend should use these groups to build the query.
     */
    groups?: ImageFilterGroup[];
}

export type ImageFilterField =
    | 'imagingSourceId'
    | 'modalities'
    | 'bodyParts'
    | 'studyDateRange'
    | 'patientId'
    | 'studyDescription';

export interface ImageFilterCondition {
    field: ImageFilterField;
    value?: any;
    conditionOperator?: 'AND' | 'OR';
}

export interface ImageFilterGroup {
    operator: 'AND' | 'OR';
    conditions: ImageFilterCondition[];
}

// ===========================
// Core Dataset Types
// ===========================

export interface Dataset {
    id: string;
    name: string;
    description?: string | null;
    ownerId: string;
    ownerName?: string;
    cohortIds: string[];
    cohortNames?: string[];
    documentFilter?: DocumentFilter | null;
    imageFilter?: ImageFilter | null;
    isLinkedToOmop: boolean;
    documentCount: number;
    imageCount: number;
    patientCount: number;
    curationJobCount: number;
    studyCount?: number;
    status: DatasetStatus;
    createdAt: string;
    updatedAt: string;
    // Derived/computed fields
    type?: 'structured' | 'unstructured' | 'imaging' | 'mixed';
    source_name?: string;
    ingestionJobIds?: string[];
}

export interface DatasetDocument {
    id: string;
    documentId: number;
    personId?: number | null;
    // Enriched fields from OMOP note table
    noteTitle?: string;
    noteDate?: string;
    noteType?: string;
    noteSource?: string;
    // Flag indicating if this document was randomly selected to fill minimum document count
    isRandom?: boolean;
    // Relevance score from DocRanker (0-100), present when docRanker is enabled
    relevanceScore?: number;
}

export interface DatasetImage {
    id: string;
    studyInstanceUid: string;
    seriesInstanceUid?: string | null;
    sopInstanceUid?: string | null;
    patientId?: string | null;
    personId?: number | null;
    // Enriched fields from DICOM
    modality?: string;
    studyDescription?: string;
    studyDate?: string;
    bodyPartExamined?: string;
}

// ===========================
// Request DTOs
// ===========================

export interface CreateDatasetRequest {
    name: string;
    description?: string;
    datasetMode?: 'cohort' | 'ingestionJob' | 'none';
    cohortIds?: string[];
    ingestionJobIds?: string[]; // Ingestion job IDs (job_id values)
    isLinkedToOmop?: boolean;
    documentFilter?: DocumentFilter;
    imageFilter?: ImageFilter;
    dataTypes?: ('structured' | 'unstructured' | 'imaging')[];
    documentIds?: number[];
    imageStudyUids?: string[];
}

export interface UpdateDatasetRequest {
    name?: string;
    description?: string;
    cohortIds?: string[];
    isLinkedToOmop?: boolean;
    status?: DatasetStatus;
}

export interface AddItemsRequest {
    documentIds?: number[];
    imageStudyUids?: string[];
}

export interface RemoveItemsRequest {
    documentIds?: number[];
    imageStudyUids?: string[];
}

// ===========================
// Response DTOs
// ===========================

export interface DatasetListResponse {
    datasets: Dataset[];
    total: number;
}

export interface DatasetImagesResponse {
    data: DatasetImage[];
    total: number;
}

export interface DatasetDocumentsResponse {
    data: DatasetDocument[];
    total: number;
}

// ===========================
// Preview Types
// ===========================

export interface DatasetPreviewRequest {
    datasetMode?: 'cohort' | 'ingestionJob' | 'none';
    cohortIds?: string[];
    ingestionJobIds?: string[];
    documentFilter?: DocumentFilter;
    imageFilter?: ImageFilter;
    dataTypes?: ('structured' | 'unstructured' | 'imaging')[];
}

export interface DatasetPreviewResponse {
    documentCount: number;
    imageCount: number;
    patientCount: number;
    sampleDocuments: DatasetDocument[];
    sampleImages: DatasetImage[];
}

// ===========================
// Query Documents Types
// ===========================

export interface QueryDocumentsRequest {
    cohortIds?: string[];
    documentFilter?: DocumentFilter;
    page?: number;
    pageSize?: number;
    search?: string;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
}

export interface QueryDocumentsResponse {
    documents: DatasetDocument[];
    total: number;
    page: number;
    pageSize: number;
}

// ===========================
// Export Types
// ===========================

export type DatasetExportFormat = 'json' | 'csv' | 'manifest';

export interface DatasetExportRequest {
    format: DatasetExportFormat;
    includeDocuments?: boolean;
    includeImages?: boolean;
}
